/**
 * Fichas e donos: o mestre cria uma ficha para alguém da mesa, dá uma ficha da biblioteca para
 * essa pessoa ou escolhe o personagem principal dela (como os "personagens do jogador" do
 * original, que são as fichas da biblioteca que pertencem a ele). Funções puras.
 */
import { roleOfModes, type LibraryItem, type RoomMember, type RoomRole, type User } from '../shared.js';

export type Person = { id: string; name: string; role: RoomRole };

/** jogadores primeiro: são eles que costumam ganhar fichas */
const ROLE_ORDER: RoomRole[] = ['player', 'gm', 'spectator'];

/** Quem está na mesa, pelo apelido (ou o nome da conta): jogadores, mestres e espectadores, cada grupo pelo nome. */
export function roomPeople(users: User[], members: Record<string, RoomMember>): Person[] {
  const people = users.map((u) => ({ id: u.id, name: members[u.id]?.nick ?? u.displayName, role: roleOfModes(members[u.id]?.modes ?? []) }));
  return people.sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) || a.name.localeCompare(b.name));
}

/** As fichas de alguém, na ordem da biblioteca. */
/**
 * O nome do token que nasce da foto da pessoa arrastada para a cena: o apelido nesta mesa, senão o
 * nome da ficha do personagem principal, senão o nome da conta.
 */
export const tokenNameOf = (member: Pick<RoomMember, 'nick'>, character: Pick<LibraryItem, 'name'> | null, user: Pick<User, 'displayName'>): string =>
  member.nick ?? character?.name ?? user.displayName;

/**
 * Os dois nomes do cartão em Participantes (BRAZ-31): em cima, o mesmo do token; embaixo, a conta.
 * `account` é null quando o de cima já é a conta (o cartão põe "você" ou "conectado" no lugar).
 */
export function cardNamesOf(member: Pick<RoomMember, 'nick'>, character: Pick<LibraryItem, 'name'> | null, user: Pick<User, 'displayName'>): { name: string; account: string | null } {
  const name = tokenNameOf(member, character, user);
  return { name, account: name === user.displayName ? null : user.displayName };
}

export const sheetsOf = (library: LibraryItem[], userId: string): LibraryItem[] => library.filter((i) => i.kind === 'sheet' && i.ownerId === userId);

/** As fichas que podem ir para alguém: as que ainda não são dela. */
export const sheetsToGive = (library: LibraryItem[], userId: string): LibraryItem[] => library.filter((i) => i.kind === 'sheet' && i.ownerId !== userId);
